import React, { FC, ReactElement } from 'react';
import {
    AppBar,
    Box,
    Button,
    ColorfulBar,
    IconButton,
    Stack,
    Theme,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
} from '@maas-components/core';
import { Logo as LogoBlack } from './svgs/logo_black';
import { Logo as LogoLight } from './svgs/logo_light';
import SearchField from './components/SearchField';
import useStyles from './styles';
import ShellProfile, { ProfileProps } from '../Profile';
import { Service } from '../types';
import { Apps } from '@maas-components/icons';

export interface HeaderProps extends ProfileProps {
    /**
     * habilita/desabilita o botão de menu na lateral esquerda
     */
    enableMenuButton?: boolean;
    /**
     * habilita/desabilita o logo
     */
    enableLogo?: boolean;
    /**
     * componente que será renderizado no lugar do logo
     */
    logo?: JSX.Element;
    /**
     * habilita/desabilita o avatar
     */
    enableAvatar?: boolean;
    /**
     * habilita/desabilita o campo de busca
     */
    enableSearchField?: boolean;
    /**
     * callback ao pressionar o botão de menu na lateral esquerda
     */
    onClickMenu?: (...args: unknown[]) => void;
    /**
     * serviços que serão renderizados na barra de busca
     */
    services: Service[];
    /**
     * estilo para inserir na raiz do componente
     */
    style?: React.CSSProperties;
    /**
     * A url que será utilizada para criar o link do Logo
     */
    logoUrl?: string;
    /**
     * Caso verdadeiro, vai utilizar a prop <RouterLink /> para navegar para o link da logo
     */
    isRouterEnabled?: boolean;
    /**
     * Desabilita/habilita navegação pelo link do logo
     */
    enableLogoLink?: boolean;
    /**
     * Desabilita/habilita troca de senha
     */
    enableChangePassword?: boolean;
    /**
     * callback ao trocar a senha
     */
    onChangePassword?: (...args: unknown[]) => void;
    /**
     * Componente a ser renderizado no cabeçalho da aplicação
     */
    MessageComponent?: () => ReactElement | null;
    /**
     * If true, the color prop is applied in dark mode.
     */
    enableColorDark?: boolean;
    /**
     * Componente de Link do roteamento da aplicação
     */
    RouterLink: React.ElementType;
}

const Header: FC<HeaderProps> = ({
    enableMenuButton = true,
    enableLogo = true,
    logo,
    enableAvatar = true,
    enableSearchField = true,
    onClickMenu,
    services,
    style,
    logoUrl = '/',
    isRouterEnabled = false,
    enableLogoLink = true,
    MessageComponent = () => <></>,
    enableColorDark,
    RouterLink,
    ...profileProps
}) => {
    const theme: Theme = useTheme();
    const mode = enableColorDark ? 'dark' : theme.palette.type;
    const classes = useStyles({ mode });
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const MenuButton = () => (
        <Box mr={1}>
            <IconButton onClick={onClickMenu}>
                <Apps className={classes.menuIcon} />
            </IconButton>
        </Box>
    );

    const renderLogo = () => {
        const icon = logo || (mode === 'light' ? <LogoLight /> : <LogoBlack />);

        if (!enableLogoLink) return icon;

        if (isRouterEnabled) {
            return (
                <RouterLink data-testid="router-link" to={logoUrl} className={classes.logoContainer}>
                    {icon}
                </RouterLink>
            );
        }

        return (
            <a href={logoUrl} className={classes.logoContainer}>
                {icon}
            </a>
        );
    };

    return (
        <AppBar position="fixed" elevation={isMobile ? 0 : 1} className={classes.appBar} style={style}>
            <ColorfulBar disableGutters />
            <Toolbar className={classes.toolbar}>
                {enableMenuButton && isMobile && <MenuButton />}
                {enableLogo && (
                    <Box data-testid="logo" className={classes.innerLogoContainer}>
                        {renderLogo()}
                    </Box>
                )}
                <Box display="flex" justifyContent="flex-end" flex={1}>
                    <Box p={2} display="flex" justifyContent={isMobile ? 'flex-end' : 'center'} flex={1}>
                        {enableMenuButton && !isMobile && (
                            <Box mr={2} display="flex" alignItems="center">
                                <Button
                                    onClick={onClickMenu}
                                    className={classes.menuButton}
                                    color="default"
                                    variant="text"
                                >
                                    <Stack spacing={1} direction="row">
                                        <Apps className={classes.menuIcon} />
                                        <Typography variant="inherit">Menu</Typography>
                                    </Stack>
                                </Button>
                            </Box>
                        )}
                        {enableSearchField && <SearchField mode={mode} services={services} />}
                    </Box>
                    <Box alignItems="center" display="flex" paddingRight={2}>
                        <MessageComponent />
                    </Box>
                    {enableAvatar && (
                        <Box display="flex" justifyContent="flex-end" alignItems="center">
                            <ShellProfile {...profileProps} enableColorDark={enableColorDark} />
                        </Box>
                    )}
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default Header;
